import React, { CSSProperties, forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { Button_Props_overrides, ButtonTypes, CreateButtonStyle, StyledButtonProps } from '../../Definitions/buttonTypes'
import { styled, StyleObject } from 'styletron-react'
import { glyphs, frames, fonts, colors, useMediaQuery } from '../../Styles';
import { CreateListStyle, ListStyle_Types } from '../../Definitions';
import { createPortal } from 'react-dom';

// Styled components
const StyledButton = styled<"button", StyledButtonProps>("button", (props) => {
  const mobile = useMediaQuery(`(max-width: 650px)`);

  
  
  const styles = CreateButtonStyle({
    buttonType: props.$buttonType,
    focus_view: props.$focus_view,
    hasstartenhancer: props.$hasstartenhancer ?? false,
    hasEndEnhancer: props.$hasEndEnhancer ?? false,
    overrides: props.$overrides,
  });

  const disabled = (props as any).disabled ?? false;

  return {
    cursor: "pointer",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
    border: "none",
    ...styles.baseStyles,
    ":hover": disabled || 'ontouchstart' in window ? {} : styles.hoverStyles,
    ":disabled": {
      cursor: "not-allowed",
      ...styles.disabledStyles,
    },
  } as any;
});

const Content = styled<"div", {}>("div", (props) => ({}));
const SubContent = styled<"div", {}>("div", (props) => ({}));
const StartEnhancer = styled<"div", {}>("div", (props) => ({}));
const EndEnhancer = styled<"div", {}>("div", (props) => ({}));

// #region | Button
export interface Button_Props {
  content: string;
  subContent?: string;
  startEnhancer?: JSX.Element | string;
  endEnhancer?: JSX.Element | string;
  buttonType?: ButtonTypes;
  disabled?: boolean;
  onClick?: () => void;
  focus_view?: boolean;
  overrides?: Button_Props_overrides;
}

const _NewButton: React.ForwardRefRenderFunction<unknown, Button_Props> = ({ content, subContent, startEnhancer, endEnhancer, buttonType = ButtonTypes.primaryButton, disabled, focus_view, overrides, onClick}, ref) => {
  const styles = useMemo(() => CreateButtonStyle({ buttonType, focus_view, hasstartenhancer: !!startEnhancer, hasEndEnhancer: !!endEnhancer, overrides  }), [buttonType, focus_view, startEnhancer, endEnhancer, overrides]);

  const enhancerFromType = (enhancer: JSX.Element | string | undefined, enhancerStyles: CSSProperties) => {
    if (!enhancer) return null;
    return typeof enhancer === "string"
      ? <img alt="enhancer" src={enhancer} style={enhancerStyles} />
      : <div style={enhancerStyles}>{enhancer}</div>;
  };

  const startEnhancerFromType = enhancerFromType(startEnhancer, styles.startEnhancerStyles);
  const endEnhancerFromType = enhancerFromType(endEnhancer, styles.endEnhancerStyles);

  const hasStartEnhancer = startEnhancerFromType != null;
  const hasEndEnhancer = endEnhancerFromType != null;

  return (
    <StyledButton
      ref={ref as React.Ref<HTMLButtonElement>}
      $buttonType={buttonType}
      $focus_view={focus_view}
      $overrides={overrides}
      disabled={disabled}
      $hasstartenhancer={hasStartEnhancer}
      $hasEndEnhancer={hasEndEnhancer}
      onClick={onClick}
    >
      {hasStartEnhancer && <StartEnhancer style={styles.startEnhancerStyles}>{startEnhancerFromType}</StartEnhancer>}
      {content && <Content style={styles.contentStyles}>{content}</Content>}
      {subContent && <SubContent style={styles.subContentStyles}>{subContent}</SubContent>}
      {hasEndEnhancer && <EndEnhancer style={styles.endEnhancerStyles}>{endEnhancerFromType}</EndEnhancer>}
    </StyledButton>
  );
};

const NewButton = forwardRef(_NewButton);
export default NewButton;
// #endregion

// #region | Button List
interface ButtonList_Props {
  contents: string[]
  startEnhancers?: (JSX.Element | undefined)[]
  endEnhancers?: (JSX.Element | undefined)[]
  buttonType?: ButtonTypes
  disabled?: boolean[] | ((index: any) => boolean)
  onClick?: (Btn_Index: number) => void
  focus_view?: boolean;
  overrides?: Button_Props_overrides | ((index: number) => Button_Props_overrides);
  listOverrides?: {
    listStyle?: ListStyle_Types
    listGap?:string
    listWidth?: string
  }
  popOverrides?: {
    hasPop?: boolean
    buttonType?: ButtonTypes
    openContent?: string
    closeContent?: string
    openEnhancer?: JSX.Element
    closeEnhancer?: JSX.Element
    popoverStyle?: CSSProperties
    openCloseStyle?: Button_Props_overrides;
  }
}

export const NewButtonList = ({ contents, focus_view, overrides, buttonType, onClick, startEnhancers, endEnhancers, disabled, listOverrides, popOverrides }: ButtonList_Props) => { 
  // const buttonRefs = useRef<Button_PropHandle[]>([])
  const buttonListRef = useRef<HTMLDivElement>(null);
  const styles = useMemo(() => CreateButtonStyle({ buttonType }), [buttonType]);

  const { listDirection } = useMemo(() => CreateListStyle(listOverrides?.listStyle), [listOverrides?.listStyle]);
  const _listGap = listOverrides?.listGap ? { gap: listOverrides.listGap } : { gap: "5px" };
  const _listWidth = listOverrides?.listWidth ? { width: listOverrides.listWidth } : { width: "fit-content" }

  const [showList, setShowList] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const _resButtonType = popOverrides?.buttonType ? popOverrides.buttonType : ButtonTypes.textButton
  const _resOpenContent = popOverrides?.openContent ? popOverrides.openContent : ""
  const _resCloseContent = popOverrides?.closeContent ? popOverrides.closeContent : ""
  const _resOpenEnhancer = popOverrides?.openEnhancer ? popOverrides.openEnhancer : <>{glyphs.Setting({size: "25px"})}</>
  const _resCloseEnhancer = popOverrides?.closeEnhancer ? popOverrides.closeEnhancer : <>{glyphs.Close({ size: "25px" })}</>
  const _resPopoverStyle = popOverrides?.popoverStyle
  const _resOpenCloseStyle = popOverrides?.openCloseStyle
  
  
  const Item_List = useMemo(() => {
    return contents.map((_contents, index) => {
      var _startEnhancers = startEnhancers ? startEnhancers[index] : undefined
      var _endEnhancers = endEnhancers ? endEnhancers[index] : undefined
      var _disabled = Array.isArray(disabled) 
        ? disabled[index] 
        : typeof disabled === 'function' 
        ? disabled(index)
        : undefined;
        const _overrides = typeof overrides === 'function' ? overrides(index) : overrides;
  
        var view = [(
          <NewButton key={"List_Input_" + index} 
            content={_contents} 
            startEnhancer={_startEnhancers}
            endEnhancer={_endEnhancers}
            overrides={_overrides}
            onClick={() => {
              if (popOverrides?.hasPop) {
                setShowList(false);
              }
              if (onClick) onClick(index)
            }}
            disabled={_disabled}
            focus_view={focus_view}
            buttonType={popOverrides?.hasPop ? _resButtonType : buttonType}
          />
        )]
        return view
      })
  }, [contents, startEnhancers, endEnhancers, disabled, overrides, focus_view, popOverrides?.hasPop, _resButtonType, buttonType, onClick])
  
  // #region | Handle click outside responsive button list
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        listRef.current && 
        !listRef.current.contains(event.target as Node) && 
        buttonListRef.current && 
        !buttonListRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    };

    if (showList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showList]);
  // #endregion

  return (
    <div ref={buttonListRef} style={{ position: "relative", ..._listWidth }}>
      {popOverrides?.hasPop ? (
        <>
          <NewButton
            content={showList ? _resCloseContent : _resOpenContent}
            onClick={() => setShowList(showList ? false : true)}
            buttonType={buttonType}
            startEnhancer={<>{showList ? _resCloseEnhancer : _resOpenEnhancer}</>}
            overrides={ _resOpenCloseStyle}
          />
          {showList && createPortal(
            <div
              ref={listRef}
              style={{
                ...listDirection as React.CSSProperties,
                ..._listGap,
                position: "absolute",
                top: "30px",
                right: "5px",
                zIndex: "9999",
                ..._resPopoverStyle
              }}>
            
            {Item_List}
            </div>, buttonListRef.current!)}
      </>
      ) : (
        <div style={{ ...listDirection as React.CSSProperties, ..._listGap }}>
          {Item_List}
        </div>
      )}
    </div>
  )

}
// #endregion